import { ColumnButton } from 'lib/components/ColumnButton';
import useAsyncEffect from 'lib/frontend/hooks/useAsyncEffect';
import { OrderModel } from 'lib/model/objects/orderModel';
import React, { useState } from 'react';
import { selectActiveOrganizationModel } from 'redux/auth';
import { useAppSelector } from 'redux/hooks';
import { logAndCaptureException } from 'utils';
import { Tooltip } from 'lib/components/Tooltip';
import { ResponseOrError } from 'lib/types/responses';
import { ColumnService } from 'lib/services/directory';
import { ConfirmCancellationModal } from './ConfirmCancellationModal';

type CancelOrderButtonProps = {
  order: OrderModel;
  onCancelOrder: (cancellationReason: string) => Promise<ResponseOrError<void>>;
};

export function CancelOrderButton({
  order,
  onCancelOrder
}: CancelOrderButtonProps) {
  const activeOrganization = useAppSelector(selectActiveOrganizationModel);
  const [showConfirmCancellationModal, setShowConfirmCancellationModal] =
    useState(false);

  const { value: canCancelData } = useAsyncEffect({
    fetchData: async () => {
      const { response: canCancelOrder, error: canCancelOrderError } =
        await order.getUserCanCancelOrder({
          activeOrganization
        });
      if (canCancelOrderError) {
        logAndCaptureException(
          ColumnService.ORDER_MANAGEMENT,
          canCancelOrderError,
          'Error determining whether user can cancel order',
          {
            orderId: order.id,
            activeOrganizationId: activeOrganization?.id,
            service: ColumnService.ORDER_MANAGEMENT
          }
        );
        return {
          canCancel: false,
          refundItems: []
        };
      }

      return canCancelOrder;
    },
    dependencies: [order.id, order.modelData.status, activeOrganization?.id],
    initialData: {
      canCancel: false,
      refundItems: [],
      reason: undefined
    }
  });

  const refundItemsTotal = (canCancelData?.refundItems || []).reduce(
    (sum, item) => sum + item.amountInCents,
    0
  );
  const refundAmount = refundItemsTotal;

  return (
    <>
      <Tooltip helpText={canCancelData?.reason ?? ''} position="bottom">
        <ColumnButton
          id="cancel-order-button"
          type="button"
          onClick={() => setShowConfirmCancellationModal(true)}
          buttonText={'Cancel order'}
          disabled={!canCancelData?.canCancel}
        />
      </Tooltip>
      {showConfirmCancellationModal && (
        <ConfirmCancellationModal
          onClose={() => setShowConfirmCancellationModal(false)}
          onCancelOrder={onCancelOrder}
          orderId={order.id}
          refundAmount={refundAmount}
        />
      )}
    </>
  );
}
